import React, {useContext, useState, useEffect} from 'react';
import {Link} from 'react-router-dom';
import HeadingAndExpandableBody from '../UI/HeadingAndExpandableBody';
import {Context} from '../../context';
import Button from '../UI/Button';
import Pagination from '../UI/Pagination';
import {
  dollarAmount,
  getDateString,
  getPaymentMethodIcon
} from '../../helperFunctions';

function OrderHistory(props) {
  const {account, content, localeText, fetchSingleOrderDetails, getTicketName} =
    useContext(Context);

  const [orderHistory, setOrderHistory] = useState();
  const [selectedOrderHistory, setSelectedOrderHistory] = useState(null);
  const [paginatedList, setPaginatedList] = useState([]);
  const [singleOrderDetails, setSingleOrderDetails] = useState(null);
  const [itemsPerPage] = useState(10);

  const [bonusValueProductID, setBonusValueProductID] = useState(null);
  useEffect(() => {
    setBonusValueProductID(
      content?.catalog?.products?.find(
        product => product?.properties?.fareType === 'bonus_stored_value'
      )?.id
    );
  }, [content]);

  useEffect(() => {
    if (account.orderHistory) {
      // consolidate orders with shared orderId's
      const newLineItems = [];
      for (const lineItem of account.orderHistory.lineItems) {
        const duplicateIndex = newLineItems.findIndex(
          newLineItem => newLineItem.orderId === lineItem.orderId
        );

        // exclude bonus values in order total
        if (lineItem.productId !== bonusValueProductID) {
          // if duplicate found, add amount this instance's amount first instance
          if (duplicateIndex >= 0)
            newLineItems[duplicateIndex].amount += lineItem.amount;
          else newLineItems.push({...lineItem});
        }
      }

      setOrderHistory(newLineItems);
    }
  }, [account, bonusValueProductID]);

  const singleOrderDetailsClickHandler = dataObj => {
    fetchSingleOrderDetails(dataObj.orderId).then(fetchedSingleOrderDetails => {
      setSingleOrderDetails(fetchedSingleOrderDetails);
      setSelectedOrderHistory(dataObj);
    });
  };

  const orderHistoryDetails = selectedOrderHistory && singleOrderDetails && (
    <div className='flex flex-col w-full'>
      <div className='hidden sm:h-[26px]' />
      <div className='sm:border-t w-full p-4'>
        <table
          //* Desktop
          className='w-full hidden sm:table'
        >
          <thead>
            <tr className='border-b'>
              <th className='pr-3 text-left'>
                {localeText('rw_title_pass', 'Pass')}
              </th>
              <th className='px-3 text-left'>
                {localeText('rw_title_created', 'Created')}
              </th>
              <th className='px-3 text-left'>#</th>
              <th className='px-3 text-left'>
                {localeText('rw_title_amount', 'Amount')}
              </th>
            </tr>
          </thead>
          <tbody>
            {singleOrderDetails.lineItems.map((lineItem, index) => (
              <tr key={index}>
                <td className='pr-3 align-top'>
                  {getTicketName(lineItem.productId)}
                </td>
                <td className='px-3 align-top'>
                  {getDateString(singleOrderDetails.created, 'date')}
                </td>
                <td className='px-3 align-top'>{lineItem.qty}</td>
                <td
                  className={`px-3 align-top ${
                    lineItem.productId.toString() ===
                      bonusValueProductID.toString() /* highlight bonus values*/ &&
                    'text-green-500'
                  }`}
                >
                  {dollarAmount(lineItem.price)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <table
          //* Mobile
          className='sm:hidden font-normal w-full'
        >
          {singleOrderDetails.lineItems.map((lineItem, index) => (
            <tbody key={index} className='border-b last:border-b-0'>
              <tr>
                <td className='align-top pr-2 pt-2 font-semibold'>
                  {localeText('rw_title_pass', 'Pass')}
                </td>
                <td className='pt-2'>{getTicketName(lineItem.productId)}</td>
              </tr>
              <tr>
                <td className='align-top pr-2 font-semibold'>
                  {localeText('rw_title_created', 'Created')}
                </td>
                <td className=''>
                  {getDateString(singleOrderDetails.created, 'date')}
                </td>
              </tr>
              <tr>
                <td className='align-top pr-2 font-semibold'>qty</td>
                <td className=''>{lineItem.qty}</td>
              </tr>
              <tr>
                <td className='align-top pr-2 pb-2 font-semibold'>
                  {localeText('rw_title_amount', 'Amount')}
                </td>
                <td
                  className={`pb-2 ${
                    lineItem.productId.toString() ===
                      bonusValueProductID.toString() /* highlight bonus values*/ &&
                    'text-green-500'
                  }`}
                >
                  {dollarAmount(lineItem.price)}
                </td>
              </tr>
            </tbody>
          ))}
        </table>
        <div className='flex justify-end items-baseline bg-lightGray2 p-3 mt-4 gap-4'>
          {localeText('rw_title_total', 'Total')}:
          <span className='font-bold text-2xl'>
            {dollarAmount(
              singleOrderDetails.paymentInfo
                .map(singlePaymentInfo => parseInt(singlePaymentInfo.amount))
                .reduce((partialSum, a) => partialSum + a, 0)
            )}
          </span>
        </div>
        <div className=''>
          <div className='mt-4 border-b mb-2 font-semibold'>
            {localeText('rw_header_billing_information', 'Billing Information')}
          </div>
          {singleOrderDetails.paymentInfo.map((singlePaymentInfo, index) => {
            const paymentMethodSrc = getPaymentMethodIcon(
              content,
              singlePaymentInfo
            );

            if (singlePaymentInfo?.processor === 'courtesy') {
              return (
                <p key={index}>
                  {localeText(
                    'rw_msg_courtesy_transaction',
                    'This was a courtesy transaction and no payment information is available.'
                  )}
                </p>
              );
            }
            return (
              <p key={index} className='flex gap-4 items-center'>
                {paymentMethodSrc && (
                  <img
                    alt={localeText(
                      'rw_alt_payment_method_icon',
                      'Payment method icon'
                    )}
                    src={paymentMethodSrc}
                    className='h-8 w-12'
                  />
                )}
                **** {singlePaymentInfo?.last4}
              </p>
            );
          })}
        </div>
        <Link
          to={`/myaccount/single-order-history/${selectedOrderHistory.orderId}`}
        >
          <Button className='w-full'>
            {localeText('rw_button_print', 'Print')}
          </Button>
        </Link>
      </div>
    </div>
  );

  return (
    <HeadingAndExpandableBody
      headingAndExpandableBodyId='order-history'
      heading={localeText('rw_header_order_history', 'Order History')}
      expanded={props.expanded}
    >
      {orderHistory && (
        <React.Fragment>
          {orderHistory.length > 0 ? (
            <div className='flex flex-row bg-white border-b'>
              <table
                id='order-history-table'
                className={`text-left h-0 w-full ${
                  selectedOrderHistory && `sm:w-fit`
                }`}
              >
                <tbody>
                  <tr>
                    <th className='px-4 min-w-[100px]'>
                      {localeText('rw_title_order', 'Order')}
                    </th>
                    {!selectedOrderHistory && (
                      <>
                        <th className='px-4'>
                          {localeText('rw_title_created', 'Created')}
                        </th>
                        <th className='px-4'>
                          {localeText('rw_title_amount', 'Amount')}
                        </th>
                      </>
                    )}
                  </tr>
                  {paginatedList.map((dataObj, index) => {
                    const focus =
                      JSON.stringify(
                        selectedOrderHistory && selectedOrderHistory
                      ) === JSON.stringify(dataObj);
                    return (
                      <tr
                        key={index}
                        className='border-y cursor-pointer sm:hover:bg-lightGray2'
                        onClick={() =>
                          !focus && singleOrderDetailsClickHandler(dataObj)
                        }
                      >
                        <td
                          className={`px-4 py-1 flex flex-col sm:flex-row sm:items-center gap-3 justify-between ${
                            focus && 'font-bold'
                          }`}
                        >
                          <div className='flex gap-2 items-center justify-between'>
                            {dataObj.orderId}
                            <img
                              className={`h-4 w-4 cursor-pointer ${
                                !focus && 'invisible'
                              }`}
                              src='/images/clearall.png'
                              alt={localeText(
                                'rw_alt_clear_cart_icon',
                                'Clear cart icon'
                              )}
                              onClick={() => setSelectedOrderHistory(null)}
                            />
                          </div>
                          {selectedOrderHistory?.orderId ===
                            dataObj.orderId && (
                            <div className='sm:hidden w-full'>
                              {orderHistoryDetails}
                            </div>
                          )}
                        </td>
                        {!selectedOrderHistory && (
                          <>
                            <td className='px-4'>
                              {getDateString(dataObj.dateTime, 'date')}
                            </td>
                            <td className='px-4'>
                              {dollarAmount(dataObj.amount)}
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {orderHistoryDetails && (
                <div className='hidden sm:block border-l w-full'>
                  {orderHistoryDetails}
                </div>
              )}
            </div>
          ) : (
            <p className='text-center'>No orders to show</p>
          )}
          <Pagination
            {...{
              list: orderHistory,
              paginatedList,
              setPaginatedList,
              itemsPerPage,
              runOnPageJump: () => setSelectedOrderHistory(null)
            }}
          />
        </React.Fragment>
      )}
    </HeadingAndExpandableBody>
  );
}

export default OrderHistory;
